import React, { createContext, useContext, useEffect, useState } from "react";

const translations = {
  en: {
    nav: { home: "Home", about: "About", services: "Services", contact: "Contact" },
    header: { cta: "Request a quote" },
    hero: {
      kicker: "Industrial machinery, made to measure",
      l1: "STEEL",
      l2: "TAKES",
      l3a: "",
      l3b: "SHAPE.",
      sub: "We design, fabricate and assemble custom machinery and components — and keep your existing systems running at their best.",
      cta1: "Start a project",
      cta2: "Explore capabilities",
      scroll: "Scroll",
    },
    intro: {
      label: "Who we are",
      title: "One partner. Every stage, from the idea to the finished machine.",
      body: "GTECH is a young, dynamic company founded by professionals with years of experience in industrial machinery. From our plant near Verona, design, fabrication, assembly, milling, turning and CNC milling work side by side — so nothing gets lost between suppliers.",
      cta: "Our story",
    },
    departments: {
      label: "Departments",
      title: "Six departments. One roof.",
      sub: "Every project moves through our own workshops — no hand-offs, no excuses.",
      cta: "See all capabilities",
      items: [
        { name: "Design", desc: "CAD engineering and co-design: from your sketch to production-ready drawings." },
        { name: "Fabrication", desc: "Cutting, welding and structural work on steels, alloys and special materials. We also serve private customers and companies with gates, railings, fences and staircases. Heat and surface treatments via trusted external partners." },
        { name: "Assembly", desc: "Mechanical, pneumatic and hydraulic assembly, testing and commissioning." },
        { name: "Milling", desc: "Milling of custom parts to your drawing, from one-offs to small series." },
        { name: "Turning", desc: "Precision turning of shafts, bushings and rotational components." },
        { name: "CNC Milling", desc: "High-speed CNC milling with tolerances down to ±0.001 mm." },
      ],
    },
    servicesHome: {
      label: "What we do",
      title: "Built for you. Kept running by us.",
      custom: {
        title: "Custom machinery & components",
        desc: "Special machines, production equipment and precision components engineered around your process — not the other way around.",
        cta: "Request a build",
      },
      overhaul: {
        title: "Maintenance & overhaul",
        desc: "Complete overhaul, retrofit and preventive maintenance programs that give existing systems a second life.",
        cta: "Plan an intervention",
      },
    },
    stats: {
      labels: ["Years of combined experience", "Specialist departments", "Projects by our team", "Tolerances up to"],
    },
    privateSection: {
      label: "For private customers",
      title: "Custom metalwork for your home and business.",
      body: "Gates, railings, fences, staircases and mezzanines — designed, fabricated and installed with the same care we put into industrial machinery.",
      items: [
        { img: "/images/gate.webp", name: "Gates & fences" },
        { img: "/images/stairs.webp", name: "Staircases" },
        { img: "/images/gallery-mezzanine.webp", name: "Railings & mezzanines" },
      ],
      cta: "Request a quote",
    },
    ctaSection: {
      title: "LET'S BUILD IT.",
      sub: "Tell us about your machine, component or system — we reply within 24 hours.",
      btn: "Request a quote",
      or: "or write to",
    },
    footer: {
      tagline: "Special industrial machinery, engineered and built in Italy.",
      sitemap: "Sitemap",
      deptTitle: "Departments",
      contactTitle: "Contact",
      rights: "All rights reserved.",
      vat: "VAT 05217040236",
      hq: "Headquarters",
      legal: "Legal office",
    },
    about: {
      title: "OUR",
      titleAccent: "MANIFESTO.",
      sub: "We believe a machine is a promise. These are the principles we build by.",
      chapters: [
        {
          num: "01",
          title: "Origins",
          body: "GTECH was born near Verona, in Italy's industrial heartland, from professionals who have spent their entire careers in industrial machinery. A young and dynamic company — built on decades of hands-on experience in design, machining and assembly.",
        },
        {
          num: "02",
          title: "Precision",
          body: "Precision is not a specification — it is a habit. Metrology-checked processes, certified materials and tolerances down to ±0.001 mm.",
        },
        {
          num: "03",
          title: "Innovation",
          body: "We invest so that a one-off prototype and a production overhaul receive the same rigour — and the same speed.",
        },
      ],
      gallery: { label: "Gallery", title: "Inside the plant." },
      values: {
        label: "Values",
        title: "What we stand for.",
        items: [
          { t: "Craftsmanship", d: "Machines built by people who take pride in invisible details." },
          { t: "Reliability", d: "Deadlines and tolerances treated as commitments, not estimates." },
          { t: "Partnership", d: "We stay after commissioning — maintenance is where trust is proven." },
        ],
      },
      imgCaption: "Our plant: 4,000 m² of engineering, machining and assembly.",
      cta: "Meet the departments",
    },
    servicesPage: {
      title: "CAPA",
      titleAccent: "BILITIES.",
      sub: "From a single turned shaft to a complete production line — engineered, fabricated, assembled and maintained by one team.",
      coreLabel: "Core services",
      customTitle: "Custom machinery & components",
      customBody: "Special-purpose machines, automation equipment, jigs, fixtures and precision components. We co-design with your engineers, prototype in-house and deliver tested, documented equipment ready for production.",
      customPoints: ["Special machines & production equipment", "Precision components, one-off to series", "Co-design and reverse engineering", "Documentation and CE support"],
      overhaulTitle: "Maintenance & overhaul",
      overhaulBody: "Complete mechanical overhauls, retrofits and preventive maintenance contracts. We strip, measure, rebuild and recommission your systems — often exceeding original performance.",
      overhaulPoints: ["Complete machine overhaul & retrofit", "Overhaul of battery, agricultural, earth-moving and recycling-sector machinery", "Special welding, including on-site work", "Preventive maintenance programs", "Spare parts manufacturing", "On-site service & emergency support"],
      deptTitle: "The departments.",
      deptNote: "Every project is routed through the departments it needs — all inside our plant.",
      processLabel: "How we work",
      processTitle: "From brief to commissioning.",
      steps: [
        { t: "Consultation", d: "We listen, measure and map your process before quoting." },
        { t: "Engineering", d: "CAD design, simulations and a fixed, transparent proposal." },
        { t: "Fabrication", d: "Machining, welding and surface treatment in-house." },
        { t: "Assembly & testing", d: "Dry runs, metrology checks and full documentation." },
        { t: "Lifecycle support", d: "Maintenance, spares and upgrades for the machine's life." },
      ],
      cta: "Discuss your project",
    },
    contact: {
      title: "LET'S",
      titleAccent: "BUILD IT.",
      sub: "For a special-purpose machine, the overhaul of an existing system or components built to your drawings, our technical office is at your disposal. Fill in the form and we will reply within 24 hours.",
      form: {
        name: "Full name",
        email: "Work email",
        company: "Company (optional)",
        service: "What do you need?",
        servicePlaceholder: "Select a service",
        message: "Tell us about your project",
        submit: "Send enquiry",
        sending: "Sending…",
        successTitle: "Enquiry sent.",
        successBody: "Thank you for contacting us — our technical office will reply within one working day.",
        successAgain: "Send another enquiry",
        error: "Something went wrong. Please try again or write to info@gtechitalia.it",
      },
      services: ["Custom machinery", "Precision components", "Maintenance", "Overhaul / retrofit", "Milling", "Turning", "CNC milling", "Design & engineering", "Other"],
      info: { hq: "Headquarters", legal: "Legal office", call: "Call us", write: "Write to us", hours: "Working hours", hoursVal: "Mon–Fri 7:00–16:00" },
    },
    meta: {
      home: { t: "GTECH SRL — Custom Industrial Machinery, Maintenance & Overhaul", d: "GTECH SRL designs, fabricates and assembles custom industrial machinery and components. Six departments — design, fabrication, assembly, milling, turning and CNC milling — under one roof in Italy." },
      about: { t: "About GTECH SRL — Our Manifesto", d: "Since 1998: heritage, precision and innovation in custom industrial machinery. Discover the principles behind GTECH SRL." },
      services: { t: "Capabilities — GTECH SRL", d: "Custom machinery, precision components, maintenance and overhaul. Design, fabrication, assembly, milling, turning and CNC milling departments." },
      contact: { t: "Contact GTECH SRL — Request a Quote", d: "Tell us about your machine, component or system. An engineer will reply within 24 hours." },
    },
    marquee: "Design,Fabrication,Assembly,Milling,Turning,CNC Milling,Custom Machinery,Maintenance & Overhaul",
  },
  it: {
    nav: { home: "Home", about: "Chi siamo", services: "Servizi", contact: "Contatti" },
    header: { cta: "Richiedi un preventivo" },
    hero: {
      kicker: "Macchinari industriali su misura",
      l1: "L'ACCIAIO",
      l2: "PRENDE",
      l3a: "",
      l3b: "FORMA.",
      sub: "Progettiamo, fabbrichiamo e assembliamo macchinari e componenti speciali — e manteniamo i vostri impianti sempre al massimo delle prestazioni.",
      cta1: "Inizia un progetto",
      cta2: "Scopri le capacità",
      scroll: "Scorri",
    },
    intro: {
      label: "Chi siamo",
      title: "Un solo partner. Ogni fase, dall'idea alla macchina finita.",
      body: "GTECH è una realtà giovane e dinamica, nata da professionisti con anni di esperienza nei macchinari industriali. Nel nostro stabilimento vicino a Verona, progettazione, carpenteria, assemblaggio, fresatura, tornitura e fresatura CNC lavorano fianco a fianco — così nulla si perde tra un fornitore e l'altro.",
      cta: "La nostra storia",
    },
    departments: {
      label: "Reparti",
      title: "Sei reparti. Un solo stabilimento.",
      sub: "Ogni progetto attraversa le nostre officine — nessun passaggio di mano, nessuna scusa.",
      cta: "Vedi tutte le capacità",
      items: [
        { name: "Progettazione", desc: "Ingegneria CAD e co-design: dal vostro schizzo ai disegni pronti per la produzione." },
        { name: "Carpenteria", desc: "Taglio, saldatura e lavorazioni strutturali su acciai, leghe e materiali speciali. Siamo a disposizione di privati e aziende anche per cancelli, inferriate, recinzioni e scale. Trattamenti termici e superficiali in collaborazione con fornitori esterni qualificati." },
        { name: "Assemblaggio", desc: "Assemblaggio meccanico, pneumatico e oleodinamico, collaudo e messa in servizio." },
        { name: "Fresatura", desc: "Fresatura di particolari a disegno, dal pezzo unico alla piccola serie." },
        { name: "Tornitura", desc: "Tornitura di precisione di alberi, boccole e componenti di rivoluzione." },
        { name: "Fresatura CNC", desc: "Fresatura CNC ad alta velocità con tolleranze fino a ±0,001 mm." },
      ],
    },
    servicesHome: {
      label: "Cosa facciamo",
      title: "Costruiti per voi. Mantenuti da noi.",
      custom: {
        title: "Macchinari e componenti su misura",
        desc: "Macchine speciali, impianti di produzione e componenti di precisione progettati intorno al vostro processo — non il contrario.",
        cta: "Richiedi una costruzione",
      },
      overhaul: {
        title: "Manutenzione e revisione",
        desc: "Revisioni complete, retrofit e programmi di manutenzione preventiva che danno una seconda vita ai vostri impianti.",
        cta: "Pianifica un intervento",
      },
    },
    stats: {
      labels: ["Anni di esperienza del team", "Reparti specialistici", "Progetti del nostro team", "Tolleranze fino a"],
    },
    privateSection: {
      label: "Per privati e aziende",
      title: "Carpenteria su misura per la casa e l'azienda.",
      body: "Cancelli, inferriate, recinzioni, scale e soppalchi — progettati, costruiti e installati con la stessa cura che mettiamo nei macchinari industriali.",
      items: [
        { img: "/images/gate.webp", name: "Cancelli e recinzioni" },
        { img: "/images/stairs.webp", name: "Scale" },
        { img: "/images/gallery-mezzanine.webp", name: "Ringhiere e soppalchi" },
      ],
      cta: "Richiedi un preventivo",
    },
    ctaSection: {
      title: "COSTRUIAMOLO INSIEME.",
      sub: "Raccontateci la vostra macchina, componente o impianto — rispondiamo entro 24 ore.",
      btn: "Richiedi un preventivo",
      or: "oppure scrivi a",
    },
    footer: {
      tagline: "Macchinari industriali speciali, progettati e costruiti in Italia.",
      sitemap: "Mappa del sito",
      deptTitle: "Reparti",
      contactTitle: "Contatti",
      rights: "Tutti i diritti riservati.",
      vat: "P.IVA 05217040236",
      hq: "Sede operativa",
      legal: "Sede legale",
    },
    about: {
      title: "IL NOSTRO",
      titleAccent: "MANIFESTO.",
      sub: "Crediamo che una macchina sia una promessa. Questi sono i principi con cui costruiamo.",
      chapters: [
        {
          num: "01",
          title: "Le origini",
          body: "GTECH nasce vicino a Verona, nel cuore industriale italiano, da professionisti che hanno dedicato la loro carriera ai macchinari industriali. Una realtà giovane e dinamica, costruita su decenni di esperienza concreta in progettazione, lavorazione e assemblaggio.",
        },
        {
          num: "02",
          title: "Precisione",
          body: "La precisione non è una specifica — è un'abitudine. Processi verificati in metrologia, materiali certificati e tolleranze fino a ±0,001 mm.",
        },
        {
          num: "03",
          title: "Innovazione",
          body: "Investiamo affinché un prototipo unico e la revisione di un impianto ricevano lo stesso rigore — e la stessa velocità.",
        },
      ],
      gallery: { label: "Galleria", title: "Dentro lo stabilimento." },
      values: {
        label: "Valori",
        title: "Ciò in cui crediamo.",
        items: [
          { t: "Artigianalità", d: "Macchine costruite da persone orgogliose dei dettagli invisibili." },
          { t: "Affidabilità", d: "Scadenze e tolleranze trattate come impegni, non stime." },
          { t: "Partnership", d: "Restiamo dopo il collaudo — la manutenzione è dove si dimostra la fiducia." },
        ],
      },
      imgCaption: "Il nostro stabilimento: 4.000 m² di progettazione, lavorazione e assemblaggio.",
      cta: "Scopri i reparti",
    },
    servicesPage: {
      title: "CAPA",
      titleAccent: "CITÀ.",
      sub: "Dal singolo albero tornito alla linea di produzione completa — progettata, fabbricata, assemblata e mantenuta da un solo team.",
      coreLabel: "Servizi principali",
      customTitle: "Macchinari e componenti su misura",
      customBody: "Macchine speciali, impianti di automazione, attrezzature e componenti di precisione. Progettiamo insieme ai vostri ingegneri, prototipiamo internamente e consegniamo attrezzature collaudate e documentate, pronte per la produzione.",
      customPoints: ["Macchine speciali e impianti di produzione", "Componenti di precisione, dal pezzo unico alla serie", "Co-design e reverse engineering", "Documentazione e supporto CE"],
      overhaulTitle: "Manutenzione e revisione",
      overhaulBody: "Revisioni meccaniche complete, retrofit e contratti di manutenzione preventiva. Smontiamo, misuriamo, ricostruiamo e rimettiamo in servizio i vostri impianti — spesso superando le prestazioni originali.",
      overhaulPoints: ["Revisione completa e retrofit di macchine", "Revisione macchinari per batterie, macchine agricole, movimento terra e settore recycling", "Saldature speciali, anche in opera", "Programmi di manutenzione preventiva", "Produzione di ricambi", "Assistenza in loco e pronto intervento"],
      deptTitle: "I reparti.",
      deptNote: "Ogni progetto viene instradato nei reparti di cui ha bisogno — tutti dentro il nostro stabilimento.",
      processLabel: "Come lavoriamo",
      processTitle: "Dal brief al collaudo.",
      steps: [
        { t: "Consulenza", d: "Ascoltiamo, misuriamo e mappiamo il vostro processo prima del preventivo." },
        { t: "Ingegneria", d: "Progettazione CAD, simulazioni e una proposta fissa e trasparente." },
        { t: "Fabbricazione", d: "Lavorazioni meccaniche, saldatura e trattamenti interni." },
        { t: "Assemblaggio e collaudo", d: "Prove a vuoto, controlli metrologici e documentazione completa." },
        { t: "Supporto continuo", d: "Manutenzione, ricambi e aggiornamenti per tutta la vita della macchina." },
      ],
      cta: "Parliamo del tuo progetto",
    },
    contact: {
      title: "COSTRUIAMOLO",
      titleAccent: "INSIEME.",
      sub: "Per la realizzazione di un macchinario speciale, la revisione di un impianto esistente o la produzione di componenti a disegno, il nostro ufficio tecnico è a vostra disposizione. Compilate il modulo: risponderemo entro 24 ore.",
      form: {
        name: "Nome e cognome",
        email: "Email aziendale",
        company: "Azienda (facoltativo)",
        service: "Di cosa avete bisogno?",
        servicePlaceholder: "Seleziona un servizio",
        message: "Raccontateci il vostro progetto",
        submit: "Invia richiesta",
        sending: "Invio in corso…",
        successTitle: "Richiesta inviata.",
        successBody: "Grazie per averci contattato — il nostro ufficio tecnico risponderà entro un giorno lavorativo.",
        successAgain: "Invia un'altra richiesta",
        error: "Qualcosa è andato storto. Riprova o scrivici a info@gtechitalia.it",
      },
      services: ["Macchinari su misura", "Componenti di precisione", "Manutenzione", "Revisione / retrofit", "Fresatura", "Tornitura", "Fresatura CNC", "Progettazione e ingegneria", "Altro"],
      info: { hq: "Sede operativa", legal: "Sede legale", call: "Chiamaci", write: "Scrivici", hours: "Orari", hoursVal: "Lun–Ven 7:00–16:00" },
    },
    meta: {
      home: { t: "GTECH SRL — Macchinari industriali su misura, manutenzione e revisione", d: "GTECH SRL progetta, fabbrica e assembla macchinari industriali e componenti su misura. Sei reparti — progettazione, carpenteria, assemblaggio, fresatura, tornitura e CNC — sotto un unico tetto in Italia." },
      about: { t: "Chi siamo — GTECH SRL", d: "Dal 1998: eredità, precisione e innovazione nei macchinari industriali su misura. Scopri i principi di GTECH SRL." },
      services: { t: "Capacità — GTECH SRL", d: "Macchinari su misura, componenti di precisione, manutenzione e revisione. Reparti di progettazione, carpenteria, assemblaggio, fresatura, tornitura e CNC." },
      contact: { t: "Contatti — GTECH SRL", d: "Raccontateci la vostra macchina, componente o impianto. Un ingegnere risponde entro 24 ore." },
    },
    marquee: "Progettazione,Carpenteria,Assemblaggio,Fresatura,Tornitura,Fresatura CNC,Macchinari su misura,Manutenzione & Revisione",
  },
};

const LanguageContext = createContext(null);

const get = (obj, path) =>
  path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("gtech-lang") || "en");

  const setLang = (l) => {
    localStorage.setItem("gtech-lang", l);
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (path) => {
    const v = get(translations[lang], path);
    if (v !== undefined) return v;
    const fallback = get(translations.en, path);
    return fallback !== undefined ? fallback : path;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
